import { useContext } from 'react';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import type { ContextNodeFragment } from '@aurora/shared-generated/types/graphql-types';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import { getLog } from '@aurora/shared-utils/log';
import type {
  CategoryDetailsQuery,
  CategoryDetailsQueryVariables
} from '../../../../types/graphql-types';
import categoryDetailsQuery from '../CategoryDetails.query.graphql';
import type { QueryResult } from '@apollo/client';

const log = getLog(module);

export interface CategoryDetailsQueryData {
  /**
   * Whether the category data is loaded or not
   */
  loading: boolean;
  /**
   * Merged context node and board details data
   */
  categoryData: Pick<CategoryDetailsQuery, 'category'> & Pick<ContextNodeFragment, 'description'>;
  /**
   * Query result
   */
  queryResult: QueryResult<CategoryDetailsQuery, CategoryDetailsQueryVariables>;
}

/**
 * This hook is used to fetch details of a category
 * @param variables the variables that fetch fields conditionally from the GQL call
 * @param skip A boolean to indicate whether the query should be called or not
 * @returns QueryResult of type CategoryDetailsQuery
 *
 * @author Guido Mininno
 */
export default function useCategoryDetails(
  variables: CategoryDetailsQueryVariables,
  skip = false
): CategoryDetailsQueryData {
  const { contextNode } = useContext(AppContext);
  const queryResult = useQueryWithTracing<CategoryDetailsQuery, CategoryDetailsQueryVariables>(
    module,
    categoryDetailsQuery,
    { variables, skip }
  );

  if (queryResult?.loading) {
    return {
      loading: true,
      categoryData: null,
      queryResult
    };
  }

  if (queryResult?.error) {
    log.error(queryResult?.error, 'Error getting category details for %O', variables);
  }

  return {
    loading: false,
    categoryData: {
      ...queryResult?.data?.category,
      description: contextNode.description
    },
    queryResult
  };
}
